/* Toggle */
.label {
  align-items: center;
  user-select: none;
  display: inline-flex;
  gap: var(--spaces-8);
  width: auto;

  &:where([data-block]) {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  &:where([data-disabled]) {
    cursor: not-allowed;
  }

  &:where(:not([data-disabled]):not([data-read-only])) {
    cursor: pointer;
  }

  &:where([data-read-only]) {
    cursor: default;
  }
}

.toggleLabelText {
  user-select: none;
}

/* ToggleSwitch */
.switch {
  border-radius: 8px;
  cursor: pointer;
  outline: none;

  &:where([data-focus-visible]) {
    box-shadow: 0 0 0 1px var(--colors-border-interactive-focused);
  }
}

.switchBackground {
  fill: var(--colors-background-interactive-off);
  transition: fill 0.3s var(--transitions-eases-primary);

  :where([data-checked]) > & {
    fill: var(--colors-brand-primary);
  }

  :where([data-disabled]) > & {
    fill: var(--colors-background-interactive-disabled);
  }
}

.switchCircle {
  fill: var(--colors-components-select-knob-interactive-on);
  transition: fill 0.3s var(--transitions-eases-primary),
    x 0.3s var(--transitions-eases-primary),
    width 0.3s var(--transitions-eases-primary);

  :where([data-disabled]) > & {
    fill: var(--colors-components-select-knob-interactive-off);
  }
}

/* ToggleCheckbox */
.checkbox {
  --vapor-toggle-color: var(--colors-brand-primary);
  --vapor-toggle-icon-color: var(
    --colors-components-checkbox-interactive-icon-on-color
  );
  border-radius: var(--radii-4);
  height: var(--component-sizes-16);
  position: relative;
  transition-property: box-shadow, background-color;
  transition-duration: 0.2s;
  transition-timing-function: var(--transitions-eases-primary);
  width: var(--component-sizes-16);
  outline: none;

  /* Primary variant */
  &:where([data-variant='primary']) {
    --vapor-toggle-icon-color: var(
      --colors-components-checkbox-interactive-icon-on-color
    );
    background-color: transparent;
    box-shadow: inset 0 0 0 1px var(--colors-components-checkbox-border);
  }

  &:where([data-variant='primary']):where(:not([data-disabled]):not([data-checked]):not([data-indeterminate])):where([data-hovered]) {
    box-shadow: inset 0 0 0 1px var(--colors-border-interactive-hover);
  }

  &:where([data-variant='primary']):where(:not([data-disabled]):not([data-checked]):not([data-indeterminate])):where([data-pressed]) {
    box-shadow: inset 0 0 0 2px var(--colors-border-interactive-hover);
  }

  &:where([data-variant='primary']):where([data-checked]),
  &:where([data-variant='primary']):where([data-indeterminate]) {
    background-color: var(--vapor-toggle-color);
    box-shadow: none;
  }

  &:where([data-variant='primary'])[data-disabled] {
    background-color: var(--colors-background-interactive-disabled);
    box-shadow: none;
    --vapor-toggle-icon-color: var(--colors-icon-secondary-stark);
  }

  /* Muted variant */
  &:where([data-variant='muted']) {
    --vapor-toggle-icon-color: var(--vapor-toggle-color);
    background-color: var(--colors-background-component);
  }

  &:where([data-variant='muted']):where(:not([data-disabled])):where([data-hovered]) {
    box-shadow: inset 0 0 0 1px var(--colors-border-interactive-hover);
  }

  &:where([data-variant='muted']):where(:not([data-disabled])):where([data-pressed]) {
    box-shadow: inset 0 0 0 2px var(--colors-border-interactive-hover);
  }

  &:where([data-variant='muted']):where([data-checked]),
  &:where([data-variant='muted']):where([data-indeterminate]) {
    background-color: transparent;
  }

  &:where([data-variant='muted'])[data-disabled] {
    background-color: var(--colors-background-interactive-disabled);
    box-shadow: none;
    --vapor-toggle-icon-color: var(--colors-icon-secondary-stark);
  }

  /* Over-image variant */
  &:where([data-variant='over-image']) {
    --vapor-toggle-icon-color: var(
      --colors-components-checkbox-interactive-icon-on-color
    );
    background-color: var(
      --colors-components-checkbox-over-image-background-interactive-default
    );
    box-shadow: 0 0 0 1.5px
      var(
        --colors-components-checkbox-over-image-border-interactive-default
      );
  }

  &:where([data-variant='over-image']):where([data-checked]),
  &:where([data-variant='over-image']):where([data-indeterminate]) {
    background-color: var(--vapor-toggle-color);
  }

  &:where([data-variant='over-image'])[data-disabled] {
    background-color: var(--colors-background-interactive-disabled);
    --vapor-toggle-icon-color: var(--colors-icon-secondary-stark);
  }

  &:where([data-variant='over-image']):where(:not([data-disabled]):not([data-checked]):not([data-indeterminate])):where([data-hovered]) {
    box-shadow: inset 0 0 0 1.5px var(--colors-border-interactive-hover);
  }

  &:where([data-variant='over-image']):where(:not([data-disabled]):not([data-checked]):not([data-indeterminate])):where([data-pressed]) {
    box-shadow: inset 0 0 0 2px var(--colors-border-interactive-hover);
  }

  /* Focus visible ring */
  &:where([data-focus-visible])::before {
    position: absolute;
    inset: 0;
    border-radius: var(--radii-4);
    outline: 2px solid var(--colors-border-interactive-focused);
    outline-offset: 2px;
    content: '';
  }
}

.checkboxIndeterminate {
  background: var(--vapor-toggle-icon-color);
  height: var(--spaces-2);
  width: var(--spaces-8);
}

.checkboxMotionCheckmark {
  align-items: center;
  color: var(--vapor-toggle-icon-color);
  display: flex;
  inset: 0;
  justify-content: center;
  position: absolute;
}
